import React, { useState, useEffect, useMemo } from 'react';
import {
  SearchOutlined,
  PlusCircleOutlined,
  FilterOutlined,
  ReloadOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CheckOutlined,
  StopOutlined,
} from '@ant-design/icons';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { Link } from 'react-router-dom';
import {
  Table,
  Input,
  Button,
  Select,
  Space,
  Tag,
  Tooltip,
  Popconfirm,
  Typography,
  Card,
  Row,
  Col,
  Form,
  Divider,
  Dropdown,
  Menu,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { TablePaginationConfig } from 'antd/es/table';
import type {
  FilterValue,
  SorterResult,
  TableCurrentDataSource,
} from 'antd/es/table/interface';
import 'antd/dist/reset.css';
import promotionApiService, {
  PromotionResponse,
  PromotionFilterParams,
} from '../services/promotionApiService';
import { showError, showSuccess } from '../utils/notificationUtils';

interface TableParams {
  pagination?: TablePaginationConfig;
  sortField?: string;
  sortOrder?: string | null;
  filters?: Record<string, FilterValue | null>;
}

const { Title } = Typography;
const { Option } = Select;

const Promotions = () => {
  const [promotions, setPromotions] = useState<PromotionResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const hasSelected = selectedRowKeys.length > 0;

  // Table params for Ant Design Table
  const [tableParams, setTableParams] = useState<TableParams>({
    pagination: {
      current: 1,
      pageSize: 20,
      pageSizeOptions: ['20', '50', '100'],
      showSizeChanger: true,
      showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} mục`,
    },
  });

  useEffect(() => {
    // fetchPromotions();
  }, [statusFilter, typeFilter]);

  const fetchPromotions = async () => {
    try {
      setLoading(true);
      const filterParams: PromotionFilterParams = {};

      // Add status filter if selected
      if (statusFilter) {
        filterParams.status = statusFilter;
      }

      // Add type filter if selected
      if (typeFilter) {
        filterParams.type = typeFilter;
      }

      const response = await promotionApiService.getPromotions(filterParams);
      setPromotions(response.promotions || []);
    } catch (error) {
      console.error('Error fetching promotions:', error);
      showError('Lỗi', 'Không thể tải danh sách khuyến mãi');
    } finally {
      setLoading(false);
    }
  };

  // Lọc khuyến mãi theo từ khóa tìm kiếm và bộ lọc
  const filteredPromotions = useMemo(() => {
    return promotions.filter((promotion) => {
      const matchesSearchTerm =
        promotion.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        promotion.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        promotion.description.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus = statusFilter
        ? statusFilter === promotion.status
        : true;

      const matchesType = typeFilter ? promotion.type === typeFilter : true;

      return matchesSearchTerm && matchesStatus && matchesType;
    });
  }, [promotions, searchTerm, statusFilter, typeFilter]);

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    return new Intl.DateTimeFormat('vi-VN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(new Date(dateString));
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND',
    }).format(amount);
  };

  const getPromotionTypeText = (type: string) => {
    switch (type) {
      case 'percentage':
        return 'Giảm %';
      case 'fixed_amount':
        return 'Giảm tiền cố định';
      default:
        return 'Khác';
    }
  };

  const getPromotionValueText = (promotion: PromotionResponse) => {
    switch (promotion.type) {
      case 'percentage':
        return `${promotion.value}%`;
      case 'fixed_amount':
        return formatCurrency(promotion.value);
      default:
        return promotion.value.toString();
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'default';
      case 'expired':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active':
        return 'Đang hoạt động';
      case 'inactive':
        return 'Không hoạt động';
      case 'expired':
        return 'Hết hạn';
      default:
        return 'Không xác định';
    }
  };

  const handleToggleStatus = async (
    id: string | undefined,
    currentStatus: string
  ) => {
    if (!id) return;

    try {
      const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
      setLoading(true);
      await promotionApiService.updatePromotion(id, { status: newStatus });
      showSuccess('Thành công', 'Cập nhật trạng thái khuyến mãi thành công');
      fetchPromotions(); // Refresh data
    } catch (error) {
      console.error('Error toggling promotion status:', error);
      showError('Lỗi', 'Không thể cập nhật trạng thái khuyến mãi');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePromotion = async (id: string | undefined) => {
    if (!id) return;

    try {
      setLoading(true);
      await promotionApiService.deletePromotion(id);
      showSuccess('Thành công', 'Xóa khuyến mãi thành công');
      fetchPromotions(); // Refresh data
    } catch (error) {
      console.error('Error deleting promotion:', error);
      showError('Lỗi', 'Không thể xóa khuyến mãi');
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<PromotionResponse> | SorterResult<PromotionResponse>[],
    extra: TableCurrentDataSource<PromotionResponse>
  ) => {
    setTableParams({
      pagination,
      filters,
      ...sorter,
    });
  };

  const resetFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
    setTypeFilter('');
  };

  // Batch actions
  const handleBatchStatusUpdate = async (status: 'active' | 'inactive') => {
    if (selectedRowKeys.length === 0) return;

    try {
      setLoading(true);
      await promotionApiService.updatePromotionsStatus(
        selectedRowKeys.map((key) => String(key)),
        status
      );
      showSuccess(
        'Thành công',
        `${status === 'active' ? 'Kích hoạt' : 'Vô hiệu hóa'} ${
          selectedRowKeys.length
        } khuyến mãi thành công`
      );
      setSelectedRowKeys([]);
      fetchPromotions(); // Refresh data
    } catch (error) {
      console.error('Error updating promotions status:', error);
      showError(
        'Lỗi',
        `Không thể ${
          status === 'active' ? 'kích hoạt' : 'vô hiệu hóa'
        } khuyến mãi`
      );
    } finally {
      setLoading(false);
    }
  };

  // Batch delete
  const handleBatchDelete = async () => {
    if (selectedRowKeys.length === 0) return;

    try {
      setLoading(true);
      await promotionApiService.deletePromotions(
        selectedRowKeys.map((key) => String(key))
      );
      showSuccess(
        'Thành công',
        `Đã xóa ${selectedRowKeys.length} khuyến mãi thành công`
      );
      setSelectedRowKeys([]);
      fetchPromotions(); // Refresh data
    } catch (error) {
      console.error('Error deleting promotions:', error);
      showError('Lỗi', 'Không thể xóa khuyến mãi');
    } finally {
      setLoading(false);
    }
  };

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const columns: ColumnsType<PromotionResponse> = [
    {
      title: 'Mã khuyến mãi',
      dataIndex: 'code',
      key: 'code',
      render: (text) => <span className='font-medium'>{text}</span>,
    },
    {
      title: 'Tên khuyến mãi',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Loại',
      key: 'type',
      render: (_, record) => <span>{getPromotionTypeText(record.type)}</span>,
    },
    {
      title: 'Giá trị',
      key: 'value',
      render: (_, record) => <span>{getPromotionValueText(record)}</span>,
    },
    {
      title: 'Thời gian',
      key: 'dates',
      render: (_, record) => (
        <span>
          {formatDate(record.time_constraints?.start_date || '')} -{' '}
          {formatDate(record.time_constraints?.end_date || '')}
        </span>
      ),
    },
    {
      title: 'Trạng thái',
      key: 'status',
      render: (_, record) => (
        <Tag color={getStatusColor(record.status)}>
          {getStatusText(record.status)}
        </Tag>
      ),
      filters: [
        { text: 'Đang hoạt động', value: 'active' },
        { text: 'Không hoạt động', value: 'inactive' },
        { text: 'Hết hạn', value: 'expired' },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: 'Sử dụng',
      key: 'usage',
      render: (_, record) => (
        <span>
          {record.quantity.used} / {record.quantity.total || 'Không giới hạn'}
        </span>
      ),
    },
    {
      title: 'Thao tác',
      key: 'action',
      render: (_, record) => (
        <Space size='small'>
          <Tooltip
            title={record.status === 'active' ? 'Vô hiệu hóa' : 'Kích hoạt'}
          >
            <Button
              type='text'
              icon={
                record.status === 'active' ? (
                  <CloseCircleOutlined className='text-red-600' />
                ) : (
                  <CheckCircleOutlined className='text-black' />
                )
              }
              onClick={() => handleToggleStatus(record.id, record.status)}
              disabled={record.status === 'expired'}
            />
          </Tooltip>
          <Tooltip title='Xóa'>
            <Popconfirm
              title='Bạn có chắc chắn muốn xóa khuyến mãi này?'
              onConfirm={() => handleDeletePromotion(record.id)}
              okText='Có'
              cancelText='Không'
            >
              <Button
                type='text'
                icon={<DeleteOutlined className='text-red-600' />}
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    // <div className='p-6'>
    //   <Row justify='space-between' align='middle' className='mb-6'>
    //     <Col>
    //       <Title level={2} className='m-0 text-gray-900'>
    //         Quản lý khuyến mãi
    //       </Title>
    //     </Col>
    //     <Col>
    //       <Space>
    //         <Button
    //           icon={<ReloadOutlined spin={loading} />}
    //           onClick={fetchPromotions}
    //           disabled={loading}
    //         />
    //         <Button
    //           type='primary'
    //           icon={<PlusCircleOutlined />}
    //           className='bg-green-600 hover:bg-green-700'
    //         >
    //           <Link to='/admin/promotions/create' className='text-white'>
    //             Thêm khuyến mãi mới
    //           </Link>
    //         </Button>
    //       </Space>
    //     </Col>
    //   </Row>

    //   <Card className='mb-6'>
    //     <Row gutter={[16, 16]}>
    //       <Col xs={24} sm={16} md={8} lg={8}>
    //         <Input
    //           prefix={<SearchOutlined className='text-gray-400' />}
    //           placeholder='Tìm theo mã, tên khuyến mãi...'
    //           value={searchTerm}
    //           onChange={(e) => setSearchTerm(e.target.value)}
    //           allowClear
    //         />
    //       </Col>
    //       <Col xs={24} sm={8} md={4} lg={4}>
    //         <Button
    //           icon={<FilterOutlined />}
    //           onClick={() => setFilterOpen(!filterOpen)}
    //           className={filterOpen ? 'bg-gray-200' : ''}
    //         >
    //           Bộ lọc
    //         </Button>
    //       </Col>
    //       {filterOpen && (
    //         <>
    //           <Col span={24}>
    //             <Divider className='my-3' />
    //           </Col>
    //           <Col xs={24} sm={12} md={8} lg={8}>
    //             <Form.Item label='Trạng thái' className='mb-0'>
    //               <Select
    //                 placeholder='Chọn trạng thái'
    //                 value={statusFilter}
    //                 onChange={setStatusFilter}
    //                 allowClear
    //                 style={{ width: '100%' }}
    //               >
    //                 <Option value='active'>Đang hoạt động</Option>
    //                 <Option value='inactive'>Ngừng hoạt động</Option>
    //                 <Option value='expired'>Hết hạn</Option>
    //               </Select>
    //             </Form.Item>
    //           </Col>
    //           <Col xs={24} sm={12} md={8} lg={8}>
    //             <Form.Item label='Loại khuyến mãi' className='mb-0'>
    //               <Select
    //                 placeholder='Chọn loại khuyến mãi'
    //                 value={typeFilter}
    //                 onChange={setTypeFilter}
    //                 allowClear
    //                 style={{ width: '100%' }}
    //               >
    //                 <Option value='percentage'>Giảm %</Option>
    //                 <Option value='fixed_amount'>Giảm tiền cố định</Option>
    //               </Select>
    //             </Form.Item>
    //           </Col>
    //           <Col xs={24} sm={12} md={8} lg={8}>
    //             <Button onClick={resetFilters}>Đặt lại bộ lọc</Button>
    //           </Col>
    //         </>
    //       )}
    //     </Row>
    //   </Card>

    //   <Card>
    //     <div className='mb-4'>
    //       <Row justify='space-between' align='middle'>
    //         <Col>
    //           {hasSelected && (
    //             <span className='mr-3'>
    //               Đã chọn {selectedRowKeys.length} mục
    //             </span>
    //           )}
    //         </Col>
    //         <Col>
    //           {hasSelected && (
    //             <Space>
    //               <Popconfirm
    //                 title='Bạn có chắc chắn muốn kích hoạt các khuyến mãi đã chọn?'
    //                 onConfirm={() => handleBatchStatusUpdate('active')}
    //                 okText='Có'
    //                 cancelText='Không'
    //               >
    //                 <Button
    //                   type='primary'
    //                   icon={<CheckOutlined />}
    //                   className='bg-green-600 hover:bg-green-700'
    //                 >
    //                   Kích hoạt
    //                 </Button>
    //               </Popconfirm>
    //               <Popconfirm
    //                 title='Bạn có chắc chắn muốn vô hiệu hóa các khuyến mãi đã chọn?'
    //                 onConfirm={() => handleBatchStatusUpdate('inactive')}
    //                 okText='Có'
    //                 cancelText='Không'
    //               >
    //                 <Button danger icon={<StopOutlined />}>
    //                   Vô hiệu hóa
    //                 </Button>
    //               </Popconfirm>
    //               <Popconfirm
    //                 title='Bạn có chắc chắn muốn xóa các khuyến mãi đã chọn?'
    //                 onConfirm={handleBatchDelete}
    //                 okText='Có'
    //                 cancelText='Không'
    //               >
    //                 <Button danger icon={<DeleteOutlined />}>
    //                   Xóa
    //                 </Button>
    //               </Popconfirm>
    //             </Space>
    //           )}
    //         </Col>
    //       </Row>
    //     </div>
    //     <Table
    //       rowSelection={rowSelection}
    //       columns={columns}
    //       dataSource={filteredPromotions}
    //       rowKey='id'
    //       loading={loading}
    //       pagination={tableParams.pagination}
    //       onChange={handleTableChange}
    //       locale={{
    //         emptyText: 'Không tìm thấy khuyến mãi nào phù hợp với tìm kiếm',
    //       }}
    //     />
    //   </Card>
    // </div>
    <div className="p-6">
      <h1 className="text-2xl font-semibold text-gray-900">Quản lý khuyến mãi</h1>
      <p>Coming soon</p>
    </div>
  );
};

export default Promotions;
